import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Inner, Badge, fadeInUp } from '../styles/v2.styled';

const NAV_ITEMS = [
  { to: '/Developer', label: '작업관리', desc: '게시판과 업무 기록' },
  { to: '/Portfolio', label: 'Portfolio', desc: '프로젝트 결과물' },
  { to: '/WorkCalendar', label: 'WorkCalendar', desc: '일정과 할 일' },
];

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[50]};
`;

const Hero = styled.section`
  padding: 64px 0 44px;
  background: linear-gradient(135deg, #062f35 0%, #075e69 55%, #0a8ea3 100%);
  color: #fff;
`;

const HeroGrid = styled(Inner)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 40px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  animation: ${fadeInUp} 0.45s ease both;
`;

const Eyebrow = styled.div`
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background: #67e8f9;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0 0 14px;
  color: #fff;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 1.12;
`;

const Desc = styled.p`
  max-width: 680px;
  margin: 0;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.75;
  font-size: 1.02rem;
`;

const StatusPanel = styled.aside`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 22px;
  animation: ${fadeInUp} 0.45s 0.08s ease both;
`;

const StatusTitle = styled.div`
  font-weight: 900;
  margin-bottom: 14px;
`;

const MetricGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const Metric = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.9rem;

  strong {
    color: #fff;
  }
`;

const Body = styled(Inner)`
  padding-top: 30px;
  padding-bottom: 72px;
`;

const Nav = styled.nav`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 22px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const NavLink = styled(Link)`
  display: block;
  min-height: 86px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[400] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  padding: 18px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.neutral[900]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.neutral[900]};
  box-shadow: ${({ $active }) => ($active ? '0 10px 26px rgba(8, 145, 178, 0.12)' : 'none')};

  strong {
    display: block;
    font-size: 1rem;
    margin-bottom: 6px;
  }

  span {
    color: ${({ theme }) => theme.colors.neutral[500]};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.neutral[500]};
    font-size: 0.86rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 16px;

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ToolbarTitle = styled.div`
  h2 {
    margin: 0 0 6px;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1.28rem;
    font-weight: 900;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.neutral[500]};
    line-height: 1.55;
  }
`;

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  padding: 22px;
  overflow: hidden;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.05);

  @media (max-width: 760px) {
    padding: 14px;
  }
`;

export const LegacySurface = styled.div`
  color: ${({ theme }) => theme.colors.neutral[800]};

  .Wrapper,
  .TabControl,
  .TabPage {
    width: 100%;
    margin: 0;
    padding: 0;
    background: transparent;
  }

  h1 {
    display: none;
  }

  .SubTabPage,
  .SubNavCategory {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin: 0 0 18px !important;
    padding: 0;
    list-style: none;
  }

  .SubTabPage li,
  .SubNavCategory li {
    list-style: none;
  }

  .SubTabPage a,
  .SubNavCategory a {
    display: inline-flex;
    align-items: center;
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.neutral[0]};
    color: ${({ theme }) => theme.colors.neutral[700]};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.neutral[700]};
    text-decoration: none;
    font-weight: 800;
    cursor: pointer;
  }

  .SubTabPage .TabActive,
  .SubNavCategory .TabActive {
    border-color: ${({ theme }) => theme.colors.primary[400]};
    background: ${({ theme }) => theme.colors.primary[50]};
    color: ${({ theme }) => theme.colors.primary[700]};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
  }

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.92rem;
  }

  th {
    background: ${({ theme }) => theme.colors.neutral[50]};
    color: ${({ theme }) => theme.colors.neutral[700]};
    font-weight: 900;
  }

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    padding: 13px 12px;
  }

  a,
  button {
    color: ${({ theme }) => theme.colors.primary[700]};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
  }

  button,
  input[type='button'],
  input[type='submit'] {
    min-height: 36px;
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.neutral[0]};
    padding: 0 12px;
    font-weight: 800;
    cursor: pointer;
  }

  input,
  textarea,
  select {
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    border-radius: 8px;
    padding: 10px 12px;
    color: ${({ theme }) => theme.colors.neutral[900]};
    background: ${({ theme }) => theme.colors.neutral[0]};
  }

  .PortfolioMasterPanel,
  .M_PortfolioMasterPanel {
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.neutral[0]};
    padding: 18px !important;
    overflow: hidden;
  }

  .PortfolioMasterPanel img,
  .M_PortfolioMasterPanel img {
    border-radius: 8px;
    object-fit: cover;
  }

  @media (max-width: 760px) {
    overflow-x: auto;

    table {
      min-width: 720px;
    }
  }
`;

function WorkspaceLayout({
  active,
  title,
  description,
  statusTitle = '작업 상태',
  metrics = [],
  children,
  toolbarTitle,
  toolbarText,
  badge,
}) {
  return (
    <Page>
      <Hero>
        <HeroGrid>
          <Copy>
            <Eyebrow />
            <Title>{title}</Title>
            <Desc>{description}</Desc>
          </Copy>
          <StatusPanel>
            <StatusTitle>{statusTitle}</StatusTitle>
            <MetricGrid>
              {metrics.map((metric) => (
                <Metric key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </Metric>
              ))}
            </MetricGrid>
          </StatusPanel>
        </HeroGrid>
      </Hero>
      <Body>
        <Nav aria-label="작업실 메뉴">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} $active={active === item.to}>
              <strong>{item.label}</strong>
              <span>{item.desc}</span>
            </NavLink>
          ))}
        </Nav>
        <Toolbar>
          <ToolbarTitle>
            <h2>{toolbarTitle}</h2>
            <p>{toolbarText}</p>
          </ToolbarTitle>
          {badge && <Badge $variant="primary">{badge}</Badge>}
        </Toolbar>
        <Panel>{children}</Panel>
      </Body>
    </Page>
  );
}

export default WorkspaceLayout;
